import { Controller } from "stimulus";

// iOS 26 is known not to reset visualViewport.offsetTop back to 0 once the keyboard
// closes, so dismissal is an explicit reset (attribute + custom properties removed,
// scroll pinned to the top) rather than a recomputation from the next resize/scroll.
const KEYBOARD_CLOSE_FALLBACK_MS = 400;
const FULL_HEIGHT_TOLERANCE_PX = 1;

export default class KeyboardViewportController extends Controller {
  connect() {
    // Turbo Drive swaps <body> but never touches <html>, so a keyboard-open state left
    // behind by the previous page (e.g. focus was lost to DOM removal without a focusout
    // ever firing) would otherwise survive into this one. Clear unconditionally before
    // this instance decides its own state from scratch.
    this.clearKeyboardOpenState();

    // Nothing to key the shell off without the Visual Viewport API.
    if (!window.visualViewport) return;

    this.mediaQuery = window.matchMedia("(min-width: 768px)");
    this.rafId = null;
    this.closeFallbackTimer = null;
    this.pendingBlurTimer = null;

    this.boundMediaChange = this.handleMediaChange.bind(this);
    this.boundViewportChange = this.handleViewportChange.bind(this);
    this.boundFocusIn = this.handleFocusIn.bind(this);
    this.boundFocusOut = this.handleFocusOut.bind(this);
    this.boundPageShow = this.handlePageShow.bind(this);

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.boundMediaChange);
    } else {
      this.mediaQuery.addListener(this.boundMediaChange);
    }

    window.visualViewport.addEventListener("resize", this.boundViewportChange);
    window.visualViewport.addEventListener("scroll", this.boundViewportChange);
    this.element.addEventListener("focusin", this.boundFocusIn);
    this.element.addEventListener("focusout", this.boundFocusOut);
    window.addEventListener("pageshow", this.boundPageShow);

    this.handleViewportChange();
  }

  disconnect() {
    // Teardown keys off what connect() actually bound, not off re-checking
    // window.visualViewport, so a Turbo restore can't leave stale listeners.
    if (!this.boundViewportChange) return;

    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener("change", this.boundMediaChange);
    } else {
      this.mediaQuery.removeListener(this.boundMediaChange);
    }

    window.visualViewport.removeEventListener("resize", this.boundViewportChange);
    window.visualViewport.removeEventListener("scroll", this.boundViewportChange);
    this.element.removeEventListener("focusin", this.boundFocusIn);
    this.element.removeEventListener("focusout", this.boundFocusOut);
    window.removeEventListener("pageshow", this.boundPageShow);

    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    this.clearCloseFallbackTimer();
    this.clearPendingBlurTimer();

    // Defensive: cleans up promptly for a disconnect that isn't immediately followed by
    // this same controller reconnecting (connect()'s own unconditional clear is the one
    // guarantee against stale state, for whatever disconnect case this doesn't cover).
    this.clearKeyboardOpenState();
  }

  handlePageShow(event) {
    // pageshow fires after every load per spec, not only bfcache restores - only a restore
    // (event.persisted) can carry forward a stale keyboard-open state from before the user
    // navigated away, so a fresh load has nothing to reset.
    if (event.persisted) this.reset();
  }

  handleMediaChange() {
    // Crossing up into the desktop breakpoint clears any keyboard-open state left over
    // from mobile, since every CSS rule keyed on it is written with no breakpoint of its own.
    if (this.mediaQuery.matches) this.reset();
  }

  handleFocusIn(event) {
    this.clearPendingBlurTimer();

    if (this.mediaQuery.matches || !this.isChatPanelField(event.target)) return;

    this.clearCloseFallbackTimer();
    document.documentElement.dataset.keyboardOpen = "true";
  }

  handleFocusOut(event) {
    if (this.mediaQuery.matches || !this.isChatPanelField(event.target)) return;

    // Defer the reset by a tick: a focusout landing on another input/textarea inside the
    // chat panel (e.g. the tip amount field) is focus moving within the panel, not the
    // keyboard being dismissed, and must not tear the state down.
    this.clearPendingBlurTimer();
    this.pendingBlurTimer = window.setTimeout(() => {
      this.pendingBlurTimer = null;
      if (this.isChatPanelField(document.activeElement)) return;

      this.scheduleClose();
    }, 0);
  }

  // Stripe Elements mounts a cross-origin card iframe inline in the tip panel (a chat-panel
  // descendant): the browser reports that iframe itself, not its contents, as
  // document.activeElement, so it must count as "typing" too - otherwise tapping from the
  // composer into card entry reads as focus leaving the chat panel and closes mid-payment.
  isChatPanelField(target) {
    if (!(target instanceof Element)) return false;
    if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA" && target.tagName !== "IFRAME") return false;

    return !!target.closest("[data-controller~='chat-panel']");
  }

  handleViewportChange() {
    if (this.rafId) return;

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null;
      this.applyViewportVars();
      this.maybeCloseFromViewport();
    });
  }

  applyViewportVars() {
    if (this.mediaQuery.matches) return;

    const viewport = window.visualViewport;
    document.documentElement.style.setProperty("--live-viewport-height", `${viewport.height}px`);
    document.documentElement.style.setProperty("--live-viewport-offset-top", `${viewport.offsetTop}px`);
  }

  // Clears as soon as the visual viewport is back to (approximately) the full layout
  // viewport height, rather than always waiting out the fallback timer below, so the
  // shell doesn't sit visibly offset for the rest of a fast keyboard dismissal.
  maybeCloseFromViewport() {
    // closeFallbackTimer doubles as the "awaiting close" flag: it's only ever non-null
    // between scheduleClose() and the reset() that always follows it.
    if (this.closeFallbackTimer === null) return;

    if (window.visualViewport.height >= window.innerHeight - FULL_HEIGHT_TOLERANCE_PX) {
      this.reset();
    }
  }

  scheduleClose() {
    this.clearCloseFallbackTimer();
    // Unconditional fallback so the reset can never be skipped if the keyboard-close
    // resize event never arrives (or never reports a full-height viewport).
    this.closeFallbackTimer = window.setTimeout(() => this.reset(), KEYBOARD_CLOSE_FALLBACK_MS);
  }

  reset() {
    this.clearCloseFallbackTimer();
    this.clearPendingBlurTimer();

    this.clearKeyboardOpenState();
    window.scrollTo(0, 0);
  }

  clearKeyboardOpenState() {
    delete document.documentElement.dataset.keyboardOpen;
    document.documentElement.style.removeProperty("--live-viewport-height");
    document.documentElement.style.removeProperty("--live-viewport-offset-top");
  }

  clearCloseFallbackTimer() {
    if (this.closeFallbackTimer) {
      window.clearTimeout(this.closeFallbackTimer);
      this.closeFallbackTimer = null;
    }
  }

  clearPendingBlurTimer() {
    if (this.pendingBlurTimer) {
      window.clearTimeout(this.pendingBlurTimer);
      this.pendingBlurTimer = null;
    }
  }
}
